import React, { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import { formatLocaleDateTime } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import type { EmailOutboxStatus } from '../useData';

export function formatSendCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  }
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }
  return `0:${s.toString().padStart(2, '0')}`;
}

export type OutboxSendScheduleView =
  | { kind: 'countdown'; countdownLabel: string; sendAtLabel: string }
  | { kind: 'sending'; label: string; sendAtLabel: string | null }
  | { kind: 'soon'; label: string; sendAtLabel: string | null };

export function getOutboxSendScheduleView(
  scheduledSendAt: string | null | undefined,
  status: EmailOutboxStatus,
  nowMs: number,
): OutboxSendScheduleView | null {
  const sendAt = scheduledSendAt ? new Date(scheduledSendAt) : null;
  const sendAtMs = sendAt?.getTime() ?? null;
  const sendAtLabel = sendAt ? formatLocaleDateTime(sendAt) : '';

  if (status === 'sending') {
    return {
      kind: 'sending',
      label: 'Sending now…',
      sendAtLabel: sendAt ? `Scheduled for ${sendAtLabel}` : null,
    };
  }

  if (status !== 'queued') {
    return null;
  }

  if (sendAtMs != null && sendAtMs > nowMs) {
    const remainingSec = (sendAtMs - nowMs) / 1000;
    return {
      kind: 'countdown',
      countdownLabel: formatSendCountdown(remainingSec),
      sendAtLabel,
    };
  }

  return {
    kind: 'soon',
    label: 'Sending soon…',
    sendAtLabel: sendAt ? `Was scheduled for ${sendAtLabel}` : null,
  };
}

export function useOutboxSendSchedule(
  scheduledSendAt: string | null | undefined,
  status: EmailOutboxStatus,
): OutboxSendScheduleView | null {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (status !== 'queued') return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [scheduledSendAt, status]);

  return getOutboxSendScheduleView(scheduledSendAt, status, nowMs);
}

interface OutboxSendScheduleProps {
  scheduledSendAt: string | null | undefined;
  status: EmailOutboxStatus;
  className?: string;
  /** Emphasize countdown (list card) vs inline (modal metadata). */
  variant?: 'card' | 'inline';
}

export const OutboxSendSchedule: React.FC<OutboxSendScheduleProps> = ({
  scheduledSendAt,
  status,
  className,
  variant = 'card',
}) => {
  const { colors } = useTheme();
  const schedule = useOutboxSendSchedule(scheduledSendAt, status);

  if (!schedule) return null;

  if (schedule.kind === 'countdown') {
    if (variant === 'inline') {
      return (
        <div className={cn('text-sm', className)}>
          <span className={cn('font-medium', colors.primary)}>Sends in {schedule.countdownLabel}</span>
          <span className={cn('ml-2', colors.textMuted)}>({schedule.sendAtLabel})</span>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2 py-1.5',
          'border-blue-200 dark:border-blue-900/50 bg-blue-50/80 dark:bg-blue-950/30',
          className,
        )}
      >
        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 tabular-nums">
          Sends in {schedule.countdownLabel}
        </span>
        <span className={cn('text-xs', colors.textMuted)}>· {schedule.sendAtLabel}</span>
      </div>
    );
  }

  const label = schedule.kind === 'sending' ? schedule.label : schedule.label;
  const secondary = schedule.kind === 'sending' ? schedule.sendAtLabel : schedule.sendAtLabel;

  return (
    <div className={cn('mt-1 text-xs', colors.textMuted, className)}>
      <span className={status === 'sending' ? cn('font-medium', 'text-amber-700 dark:text-amber-300') : undefined}>
        {label}
      </span>
      {secondary && <span className="ml-1">· {secondary}</span>}
    </div>
  );
};
